import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { PermissionConstants } from 'src/app/constants/permissions';
import { OAuthService } from 'src/app/shared/services/oauth.service';
import { selectCurrentProjectId } from 'src/app/state/app.selectors';
import { SafetyService } from '../safety.service';
import { SafetyActions } from './safety.actions';
import { selectEventsScopeProjectId } from './safety.selectors';

@Injectable()
export class SafetyEffects {

  loadEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadEvents),
      // projectId 0 = every site (GET safety/all, CanManageSafetyEvents only);
      // otherwise one site (GET safety/byProject, viewers and managers, membership-checked).
      // The list component decides the scope from the user's permissions and the toggle.
      switchMap(({ projectId }) =>
        (projectId
          ? this.safetyService.getEventsByProject(projectId)
          : this.safetyService.getAllEvents()
        ).pipe(
          map((events) => SafetyActions.loadEventsSuccess({ events })),
          catchError((error) => of(SafetyActions.loadEventsFailure({ error })))
        )
      )
    )
  );

  loadSingleEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadSingleEvent),
      switchMap(({ eventId }) =>
        this.safetyService.getEvent(eventId).pipe(
          map((event) => SafetyActions.loadSingleEventSuccess({ event })),
          catchError((error) => of(SafetyActions.loadSingleEventFailure({ error })))
        )
      )
    )
  );

  loadAllEventFrequencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadAllEventFrequencies),
      switchMap(() =>
        this.safetyService.getAllEventFrequencies().pipe(
          map((frequencies) => SafetyActions.loadAllEventFrequenciesSuccess({ frequencies })),
          catchError((error) => of(SafetyActions.loadAllEventFrequenciesFailure({ error })))
        )
      )
    )
  );

  loadFrequencyByYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadFrequencyByYear),
      mergeMap(({ year, includeExternal }) =>
        this.safetyService.getFrequencyByYear(year, includeExternal).pipe(
          map((frequency) => SafetyActions.loadFrequencyByYearSuccess({ year, includeExternal, frequency })),
          catchError((error) => of(SafetyActions.loadFrequencyByYearFailure({ error })))
        )
      )
    )
  );

  loadYearlyStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadYearlyStatistics),
      switchMap(({ includeExternal }) =>
        this.safetyService.getYearlyStatistics(includeExternal).pipe(
          map((statistics) => SafetyActions.loadYearlyStatisticsSuccess({ statistics })),
          catchError((error) => of(SafetyActions.loadYearlyStatisticsFailure({ error })))
        )
      )
    )
  );

  loadEventFrequency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadEventFrequency),
      switchMap(({ projectId }) =>
        this.safetyService.getEventFrequencyByProject(projectId).pipe(
          map((eventFrequency) => SafetyActions.loadEventFrequencySuccess({ eventFrequency })),
          catchError((error) => of(SafetyActions.loadEventFrequencyFailure({ error })))
        )
      )
    )
  );

  loadLastEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadLastEvent),
      switchMap(({ projectId }) =>
        this.safetyService.getLastEvent(projectId).pipe(
          map((event) => SafetyActions.loadLastEventSuccess({ event })),
          catchError((error) => of(SafetyActions.loadLastEventFailure({ error })))
        )
      )
    )
  );

  loadDaysWithoutEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.loadDaysWithoutEvent),
      switchMap(({ projectId }) =>
        this.safetyService.getDaysWithoutEvent(projectId).pipe(
          map((days) => SafetyActions.loadDaysWithoutEventSuccess({ days })),
          catchError((error) => of(SafetyActions.loadDaysWithoutEventFailure({ error })))
        )
      )
    )
  );

  createEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.createEvent),
      mergeMap(({ event }) =>
        this.safetyService.createEvent(event).pipe(
          map((createdEvent) => SafetyActions.createEventSuccess({ event: createdEvent })),
          catchError((error) => of(SafetyActions.createEventFailure({ error })))
        )
      )
    )
  );

  updateEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.updateEvent),
      mergeMap(({ event }) =>
        this.safetyService.updateEvent(event).pipe(
          map((updatedEvent) => SafetyActions.updateEventSuccess({ event: updatedEvent })),
          catchError((error) => of(SafetyActions.updateEventFailure({ error })))
        )
      )
    )
  );

  deleteEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyActions.deleteEvent),
      mergeMap(({ eventId }) =>
        this.safetyService.deleteEvent(eventId).pipe(
          map(() => SafetyActions.deleteEventSuccess({ eventId })),
          catchError((error) => of(SafetyActions.deleteEventFailure({ error })))
        )
      )
    )
  );

  // Reload events after a create, update or delete in the scope that was last loaded.
  // Reloading every site here would 403 for a viewer pinned to "this site only".
  //
  // Scope 0 (every site) is also the store's initial value and what the statistics page
  // dispatches, so it does not prove the list was ever loaded as a manager: a viewer who
  // adds an event from the home widget arrives here with scope 0. Only reload every site
  // when the user actually holds CanManageSafetyEvents; otherwise fall back to the current
  // site, and skip the reload when there is none to fall back to.
  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SafetyActions.createEventSuccess,
        SafetyActions.updateEventSuccess,
        SafetyActions.deleteEventSuccess
      ),
      withLatestFrom(
        this.store.select(selectEventsScopeProjectId),
        this.store.select(selectCurrentProjectId)
      ),
      switchMap(([, scopeProjectId, currentProjectId]) => {
        if (scopeProjectId) {
          return of(SafetyActions.loadEvents({ projectId: scopeProjectId }));
        }
        return from(
          this.authService.hasPermission(
            PermissionConstants.SafetyEventsPermissions.CanManageSafetyEvents
          )
        ).pipe(
          map((canManage): number | null => (canManage ? 0 : currentProjectId || null)),
          filter((projectId): projectId is number => projectId !== null),
          map((projectId) => SafetyActions.loadEvents({ projectId }))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private safetyService: SafetyService,
    private store: Store,
    private authService: OAuthService
  ) {}
}
